import React, {useEffect, useState} from 'react';
import Grid from '@material-ui/core/Grid';
import {Typography} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import useScript from '../../../../../hooks/useScript';
import Loader from '@components/framework/loader/Loader';
import {ProvidersAttributes} from '../Providers';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from '../../../../../utils/SnackBarConfig';
import {environment} from '@shared/environment/environment';
import {
  APP_PRIMARY_COLOR,
  BORDER_PRIMARY_COLOR,
  DEFAULT_WHITE_COLOR,
  FONT_PRIMARY_COLOR
} from '../../../../../style/variable';
import './OpenEdge.scss';
import {
  OPEN_EDGE_INVALID_INPUT_ERROR_DATA_PATH,
  OPEN_EDGE_TOKEN_ERROR
} from './OpenEdgeConstants';
import NumberFormat from 'react-number-format';
import {useSelector} from 'react-redux';
import HelperUtils from '@shared/utils/HelperUtils';

interface OpenEdgeProps extends ProvidersAttributes {
  cardTempToken?: string;
}
const style = {
  input: {
    display: 'block',
    background: 'none',
    border: `1px solid ${BORDER_PRIMARY_COLOR}`,
    'border-radius': '5px',
    padding: '10px 12px',
    'font-size': '1rem',
    color: `${FONT_PRIMARY_COLOR}`,
    width: '100%'
  }
};

let GlobalPayments: any = undefined;
const GlobalPaymentsEnv = environment.openEdge;
const OpenEdge = (props: OpenEdgeProps): React.ReactElement => {
  useScript('https://js.paygateway.com/secure_payment/v1/globalpayments.js');
  const {enqueueSnackbar} = useSnackbar();

  const {cardTokenized} = props;
  // Component hooks
  const {t} = useTranslation();
  const [loader, setLoader] = useState<boolean>(true);
  const [submitLoader, setSubmit] = useState<boolean>(false);
  const guarantorDetailsRedux = useSelector(
    (state: any) =>
      state.payments?.baseDetails?.guarantorDetails ??
      state?.payments?.guarantorDetails
  );

  const [cardError, setCardError] = useState<{
    card: string;
    expiry: string;
    cvv: string;
    billAddress: string;
    billPostal: string;
  }>({
    cvv: '',
    card: '',
    expiry: '',
    billAddress: '',
    billPostal: ''
  });
  const billAddress = React.useRef<HTMLInputElement>(null);
  const billPostalCode = React.useRef<HTMLInputElement>(null);
  // Component States and Refs

  const validateOtherDetails = React.useCallback((): boolean => {
    if (!billAddress.current?.value) {
      setCardError({
        ...cardError,
        billAddress: 'OPEN_EDGE.ERROR.NO_BILL_ADDRESS'
      });
      enqueueSnackbar(
        t('OPEN_EDGE.ERROR.NO_BILL_ADDRESS'),
        SnackBarConfig('e')
      );
      return false;
    }
    if (!billPostalCode.current?.value) {
      setCardError({
        ...cardError,
        billPostal: 'OPEN_EDGE.ERROR.NO_BILL_POSTAL'
      });
      enqueueSnackbar(t('OPEN_EDGE.ERROR.NO_BILL_POSTAL'), SnackBarConfig('e'));
      return false;
    }
    return true;
  }, [cardError, enqueueSnackbar, t]);
  const authenticateCardDetails = React.useCallback(
    (values: any) => {
      if (!validateOtherDetails()) {
        return;
      }
      if (typeof cardTokenized === 'function') {
        let expDate = '';
        if (values?.card?.expiry_month && values?.card?.expiry_year) {
          expDate =
            values?.card?.expiry_month + ' / ' + values?.card?.expiry_year;
        }
        cardTokenized({
          billAddress: billAddress.current?.value ?? '',
          billPostal: billPostalCode.current?.value ?? '',
          expiryDate: expDate,
          maskedCardNumber: values?.card?.masked_card_number,
          temporaryToken: values?.temporary_token,
          type: 'card'
        });
      }
    },
    [cardTokenized, validateOtherDetails]
  );

  const checkGlobalPaymentsLoaded = () => {
    // @ts-ignore
    if (window.GlobalPayments) {
      // @ts-ignore
      GlobalPayments = window.GlobalPayments;

      GlobalPayments.configure({
        'X-GP-Api-Key': GlobalPaymentsEnv.payFieldsApiKey,
        'X-GP-Environment': GlobalPaymentsEnv.environment,
        enableAutocomplete: GlobalPaymentsEnv.enableAutocomplete
      });
      const cardForm = GlobalPayments.ui.form({
        fields: {
          'card-number': {
            target: '#open-edge-card-number',
            placeholder: '•••• •••• •••• ••••'
          },
          'card-expiration': {
            target: '#open-edge-card-expiration',
            placeholder: 'MM / YYYY'
          },
          'card-cvv': {
            target: '#open-edge-card-cvv',
            placeholder: '•••'
          },
          submit: {
            target: '#open-edge-submit',
            text: 'Submit'
          }
        },
        styles: {
          button: {
            background: `${APP_PRIMARY_COLOR}`,
            color: `${DEFAULT_WHITE_COLOR}`,
            'font-size': '1.125rem',
            padding: '0.8rem',
            margin: '1rem',
            'border-radius': '0.5rem',
            border: 'none',
            cursor: 'pointer'
          },
          input: style.input,
          'input.card-number': {
            'letter-spacing': '0.35rem'
          }
        }
      });

      cardForm.on('load', () => {
        setLoader(false);
        setSubmit(false);
      });
      cardForm.on('token-success', (r: any) => {
        authenticateCardDetails(r);
        setSubmit(false);
      });
      cardForm.on('token-error', (e: any) => {
        if (e.error?.code) {
          const err = e.error;
          switch (err.code) {
            case OPEN_EDGE_TOKEN_ERROR.INVALID_INPUT:
              if (Array.isArray(err.detail)) {
                if (
                  err.detail[0].data_path ===
                  OPEN_EDGE_INVALID_INPUT_ERROR_DATA_PATH.EXPIRY_YEAR
                ) {
                  setCardError({
                    billAddress: '',
                    billPostal: '',
                    card: '',
                    cvv: '',
                    expiry: 'OPEN_EDGE.ERROR.YEAR'
                  });
                } else if (
                  err.detail[0].data_path ===
                  OPEN_EDGE_INVALID_INPUT_ERROR_DATA_PATH.EXPIRY_MONTH
                ) {
                  setCardError({
                    billAddress: '',
                    billPostal: '',
                    card: '',
                    cvv: '',
                    expiry: 'OPEN_EDGE.ERROR.MONTH'
                  });
                } else if (
                  err.detail[0].data_path ===
                  OPEN_EDGE_INVALID_INPUT_ERROR_DATA_PATH.CVV
                ) {
                  setCardError({
                    billAddress: '',
                    billPostal: '',
                    expiry: '',
                    cvv: 'OPEN_EDGE.ERROR.CVV',
                    card: ''
                  });
                } else if (
                  err.detail[0].data_path ===
                  OPEN_EDGE_INVALID_INPUT_ERROR_DATA_PATH.CARD
                ) {
                  setCardError({
                    billAddress: '',
                    billPostal: '',
                    expiry: '',
                    cvv: '',
                    card: 'OPEN_EDGE.ERROR.CARD'
                  });
                }
              }
              break;
            case OPEN_EDGE_TOKEN_ERROR.INVALID_CARD:
              setCardError({
                billAddress: '',
                billPostal: '',
                expiry: '',
                cvv: '',
                card: 'OPEN_EDGE.ERROR.CARD'
              });
              break;
          }
        }
        enqueueSnackbar(e?.error?.message, SnackBarConfig('e'));
        setSubmit(false);
      });
      cardForm.on('error', (e: any) => {
        enqueueSnackbar(e?.error?.message, SnackBarConfig('e'));
        setSubmit(false);
      });
      cardForm.on('submit', 'click', () => {
        setSubmit(true);
      });
    } else {
      setTimeout(() => {
        checkGlobalPaymentsLoaded();
      }, 1000);
    }
  };

  useEffect(checkGlobalPaymentsLoaded, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="open-edge-container">
      <Grid>
        <form>
          {loader && <Loader />}
          <div className="open-edge-field">
            {!loader && (
              <Typography>
                <span>{t('OPEN_EDGE.FIELDS.CARD')}</span>
              </Typography>
            )}
            <div id="open-edge-card-number" />
            {cardError.card && <div className="error">{t(cardError.card)}</div>}
          </div>
          <div className="open-edge-field">
            {!loader && (
              <Typography>
                <span>{t('OPEN_EDGE.FIELDS.EXPIRY')}</span>
              </Typography>
            )}
            <div id="open-edge-card-expiration" />
            {cardError.expiry && (
              <div className="error">{t(cardError.expiry)}</div>
            )}
          </div>
          <div className="open-edge-field">
            {!loader && (
              <Typography>
                <span>{t('OPEN_EDGE.FIELDS.CVV')}</span>
              </Typography>
            )}
            <div id="open-edge-card-cvv" />
            {cardError.cvv && <div className="error">{t(cardError.cvv)}</div>}
          </div>
          <div className="open-edge-field">
            {!loader && (
              <>
                <Typography>
                  <span>{t('OPEN_EDGE.FIELDS.BILL_ADDRESS_1')}</span>
                </Typography>
                <input
                  type="text"
                  ref={billAddress}
                  maxLength={50}
                  placeholder={t('OPEN_EDGE.PLACEHOLDER.ADDRESS_LINE_1')}
                  defaultValue={guarantorDetailsRedux?.address_line_1 || ''}
                />
                {cardError.billAddress && (
                  <div className="error">{t(cardError.billAddress)}</div>
                )}
              </>
            )}
          </div>
          <div className="open-edge-field">
            {!loader && (
              <>
                <Typography>
                  <span>{t('OPEN_EDGE.FIELDS.BILL_ZIP_CODE')}</span>
                </Typography>
                <NumberFormat
                  getInputRef={billPostalCode}
                  placeholder={t('OPEN_EDGE.PLACEHOLDER.ZIP_CODE')}
                  isAllowed={({floatValue}) =>
                    floatValue === undefined || floatValue <= 999999999
                  }
                  defaultValue={
                    HelperUtils.formatZipCode(
                      guarantorDetailsRedux?.zip_code
                    ) || ''
                  }
                />
                {cardError.billPostal && (
                  <div className="error">{t(cardError.billPostal)}</div>
                )}
              </>
            )}
          </div>
          <div className="open-edge-field display-flex">
            <div id="open-edge-submit" className="flex" />
            {submitLoader && <Loader />}
          </div>
        </form>
      </Grid>
    </div>
  );
};

export default OpenEdge;
